<script setup lang="ts">
import { BCard, BSpinner } from 'bootstrap-vue-next';
import { computed, onMounted, ref } from 'vue';

import type { IssueFinderListingMonth } from '~/api/issueFinderListings';
import { fetchIssueFinderListings } from '~/api/issueFinderListings';
import ErrorState from '~/common/components/ErrorState.vue';
import { toMonthLabel } from '~/common/DateUtils';

import DashboardChart from './dashboard-page/DashboardChart.vue';

type CountKey = Exclude<keyof IssueFinderListingMonth, 'activity_date'>;

const IN_SERIES: CountKey[] = ['in_community', 'in_team'];
const OUT_SERIES: CountKey[] = ['picked_up_community', 'picked_up_team', 'closed_unpicked', 'withdrawn'];
const CHARTS = [
  {
    title: 'Issues that became ready to pick up',
    description: 'How many issues entered the issue finder each month, by who opened them.',
    series: IN_SERIES,
  },
  {
    title: 'Issues that left the issue finder',
    description: 'How many issues stopped being ready to pick up each month, and why.',
    series: OUT_SERIES,
  },
];
const SERIES_LABELS: Record<CountKey, string> = {
  in_community: 'Community author',
  in_team: 'Team author',
  picked_up_community: 'Picked up by community',
  picked_up_team: 'Picked up by team',
  closed_unpicked: 'Closed, not picked up',
  withdrawn: 'Withdrawn',
};
const SERIES_MEANINGS: Record<CountKey, string> = {
  in_community: 'the issue was opened by a community member',
  in_team: 'the issue was opened by a GitLab team member',
  picked_up_community: 'a community member was assigned to the issue',
  picked_up_team: 'a GitLab team member was assigned to the issue',
  closed_unpicked: 'the issue was closed while still unassigned',
  withdrawn: 'the issue left the finder without a pickup or a close, usually because its finder label was removed',
};
const SCALE_STEP = 5;
const SCALE_HEADROOM = 1.1;
const SEGMENT_LABEL_FLOOR = 0.05;
const CHART_SIZE = { width: 1200, height: 420 };
const TIME_LABEL_COUNT = 10;
const LABEL_FONT_SIZE = 12;

const rows = ref<IssueFinderListingMonth[]>([]);
const loading = ref(true);
const error = ref(false);

const graphDates = computed(() => rows.value.map(row => toMonthLabel(row.activity_date)));

const monthlyTotal = (row: IssueFinderListingMonth, series: CountKey[]): number =>
  series.reduce((sum, key) => sum + row[key], 0);
const scaleMax = computed((): number => {
  const totals = rows.value.flatMap(row => [monthlyTotal(row, IN_SERIES), monthlyTotal(row, OUT_SERIES)]);

  return Math.ceil((Math.max(...totals) * SCALE_HEADROOM) / SCALE_STEP) * SCALE_STEP;
});
const stackedBarOptions = computed(() => ({
  scaleMax: scaleMax.value,
  fontSize: LABEL_FONT_SIZE,
  hideSegmentLabelsUnder: Math.round(scaleMax.value * SEGMENT_LABEL_FLOOR),
  size: CHART_SIZE,
  timeLabelCount: TIME_LABEL_COUNT,
}));

const fetchRows = async (): Promise<void> => {
  try {
    rows.value = await fetchIssueFinderListings();
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

onMounted(fetchRows);
</script>

<template>
  <BCard title="Pick-up-ready issues in and out">
    <BSpinner v-if="loading" />
    <ErrorState v-else-if="error" />
    <p
      v-else-if="!rows.length"
      class="text-center text-muted my-5"
    >
      No data yet.
    </p>
    <template v-else>
      <section
        v-for="(chart, index) in CHARTS"
        :key="chart.title"
        :class="{ 'mt-4': index > 0 }"
      >
        <h5 class="h6 mb-1">{{ chart.title }}</h5>
        <p class="small text-muted">{{ chart.description }}</p>
        <DashboardChart
          :graph-points="rows"
          :graph-dates="graphDates"
          :all-series="chart.series"
          :series-labels="SERIES_LABELS"
          :stacked-bar-options="stackedBarOptions"
        />
        <ul
          class="list-unstyled small text-muted mt-2"
          :class="{ 'mb-0': index === CHARTS.length - 1 }"
        >
          <li
            v-for="key in chart.series"
            :key="key"
          >
            <strong>{{ SERIES_LABELS[key] }}</strong> means {{ SERIES_MEANINGS[key] }}
          </li>
        </ul>
      </section>
    </template>
  </BCard>
</template>
